'use client';

import { c as _c } from "react/compiler-runtime";
import type { SandboxStartStatus } from '@/components/chat/code-session/sandbox-start-status';
import type { AiContext } from '@/components/chat/types/ai-context';
import type { PageChatEngineChoice } from '@/data/chat/page-chat-session';
import { useEffect, useState } from 'react';
import { Shimmer } from '@/components/ai-elements/shimmer';
import { ChatRunnerView } from '@/components/chat/chat';
import { useCodeSessionChat } from '@/components/chat/code-session/use-code-session-chat';
import { useHeldUntilReady, useSidebarChatEvents, useSidebarChatRequestContext } from '@/components/chat/sidebar-chat-engine';
import { resolveChatModel } from '@/components/chat/use-chat-model-selection';
import { useSidebarSdkChat } from '@/components/chat/use-sidebar-sdk-chat';
import { Pill } from '@/components/ui/pill';
import { usePageChatEngine } from '@/data/chat/page-chat-session';
import { useChatWarm } from '@/data/chat/use-chat-warm';
import { useWarmPageChatSession } from '@/data/chat/use-warm-page-chat-session';
import { CodeSessionSync } from '@/data/code-sessions/session-sync';
import { useChatStore } from '@/lib/stores/chat-store';
import { usePrStore } from '@/lib/stores/pr-store';
type SidebarChatProps = {
  id: string;
  isPersisted: boolean;
  aiContext: AiContext;
  warmEnabled: boolean;
  enabled?: boolean;
  /**
   * The pull request's diff has loaded into `aiContext`. The chat shows without it, with the
   * pull request's summary; a message sent before it loads waits for it.
   */
  contextReady: boolean;
};

/**
 * The chat in a pull request page's sidebar. It shows at once, the same for
 * every person: the agent that answers it (`usePageChatEngine`) only decides
 * where its messages come from and go. That is the person's code session on
 * the pull request in cubic's own installations, where /code serves them and
 * they can push to it (`src/lib/code-sessions/pr-page-session.ts`), else the
 * sidebar's own chat.
 * Until the page knows which, and while a saved conversation loads, only the
 * message list waits; a message sent meanwhile, or before the pull request's
 * diff loads, is held and delivered once it can be.
 */
export function SidebarChat(t0) {
  const $ = _c(75);
  const {
    id,
    isPersisted,
    aiContext,
    warmEnabled,
    enabled: t1,
    contextReady
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const {
    owner,
    repo,
    number,
    installationId,
    pullRequestId
  } = usePrStore();
  let t2;
  if ($[0] !== enabled || $[1] !== installationId || $[2] !== pullRequestId) {
    t2 = {
      installationId,
      pullRequestId,
      enabled
    };
    $[0] = enabled;
    $[1] = installationId;
    $[2] = pullRequestId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const answer = usePageChatEngine(t2);
  const [choice, setChoice] = useState(answer);
  const [engaged, setEngaged] = useState(false);
  const storedSessionId = useChatStore(_temp);
  const latestSessionId = choice?.engine === "code_session" ? choice.sessionId : null;
  const sessionId = storedSessionId === undefined ? latestSessionId : storedSessionId;
  const hasConversation = engaged || isPersisted || sessionId !== null;
  if (answer !== undefined && (choice === undefined || !hasConversation && !sameChoice(choice, answer))) {
    setChoice(answer);
  }
  const onSession = choice?.engine === "code_session";
  const t3 = warmEnabled && choice?.engine === "chat";
  let t4;
  if ($[4] !== aiContext || $[5] !== installationId || $[6] !== number || $[7] !== owner || $[8] !== pullRequestId || $[9] !== repo || $[10] !== t3) {
    t4 = {
      aiContext,
      owner,
      repo,
      number,
      installationId,
      pullRequestId,
      enabled: t3
    };
    $[4] = aiContext;
    $[5] = installationId;
    $[6] = number;
    $[7] = owner;
    $[8] = pullRequestId;
    $[9] = repo;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  useChatWarm(t4);
  let t5;
  if ($[12] !== owner) {
    t5 = () => resolveChatModel(owner);
    $[12] = owner;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const t6 = enabled && onSession && !hasConversation;
  let t7;
  if ($[14] !== id || $[15] !== pullRequestId || $[16] !== t5 || $[17] !== t6) {
    t7 = {
      chatId: id,
      pullRequestId,
      model: t5,
      enabled: t6
    };
    $[14] = id;
    $[15] = pullRequestId;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const warmed = useWarmPageChatSession(t7);
  const setCodeSessionId = useChatStore(_temp2);
  const startNewChat = useChatStore(_temp3);
  let t8;
  let t9;
  if ($[19] !== id || $[20] !== latestSessionId || $[21] !== setCodeSessionId || $[22] !== storedSessionId) {
    t8 = () => {
      if (storedSessionId === undefined && latestSessionId !== null) {
        setCodeSessionId(latestSessionId, id);
      }
    };
    t9 = [id, latestSessionId, setCodeSessionId, storedSessionId];
    $[19] = id;
    $[20] = latestSessionId;
    $[21] = setCodeSessionId;
    $[22] = storedSessionId;
    $[23] = t8;
    $[24] = t9;
  } else {
    t8 = $[23];
    t9 = $[24];
  }
  useEffect(t8, t9);
  const {
    handleToolCall,
    handleTurnEnd,
    liveFinishedMessageId
  } = useSidebarChatEvents(id);
  const resolveRequestContext = useSidebarChatRequestContext(aiContext);
  const t10 = choice?.engine === "chat";
  let t11;
  if ($[25] !== handleToolCall || $[26] !== handleTurnEnd || $[27] !== id || $[28] !== isPersisted || $[29] !== resolveRequestContext || $[30] !== t10) {
    t11 = {
      id,
      isPersisted,
      enabled: t10,
      resolveRequestContext,
      onToolCall: handleToolCall,
      onTurnEnd: handleTurnEnd
    };
    $[25] = handleToolCall;
    $[26] = handleTurnEnd;
    $[27] = id;
    $[28] = isPersisted;
    $[29] = resolveRequestContext;
    $[30] = t10;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const sdk = useSidebarSdkChat(t11);
  const t12 = onSession ? sessionId : null;
  let t13;
  if ($[32] !== id || $[33] !== setCodeSessionId) {
    t13 = startedSessionId => setCodeSessionId(startedSessionId, id);
    $[32] = id;
    $[33] = setCodeSessionId;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== aiContext || $[36] !== handleToolCall || $[37] !== handleTurnEnd || $[38] !== id || $[39] !== onSession || $[40] !== resolveRequestContext || $[41] !== t12 || $[42] !== t13 || $[43] !== warmed) {
    t14 = {
      chatId: id,
      sessionId: t12,
      enabled: onSession,
      preloading: warmed,
      aiContext,
      resolveRequestContext,
      onStarted: t13,
      onToolCall: handleToolCall,
      onTurnEnd: handleTurnEnd
    };
    $[35] = aiContext;
    $[36] = handleToolCall;
    $[37] = handleTurnEnd;
    $[38] = id;
    $[39] = onSession;
    $[40] = resolveRequestContext;
    $[41] = t12;
    $[42] = t13;
    $[43] = warmed;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  const t15 = useCodeSessionChat(t14);
  let gone;
  let readSessionId;
  let sandbox;
  let session;
  let sessionHistoryLoading;
  let sessionRetryHistory;
  if ($[45] !== t15) {
    ({
      gone,
      historyLoading: sessionHistoryLoading,
      retryHistory: sessionRetryHistory,
      readSessionId,
      sandbox,
      ...session
    } = t15);
    $[45] = t15;
    $[46] = gone;
    $[47] = readSessionId;
    $[48] = sandbox;
    $[49] = session;
    $[50] = sessionHistoryLoading;
    $[51] = sessionRetryHistory;
  } else {
    gone = $[46];
    readSessionId = $[47];
    sandbox = $[48];
    session = $[49];
    sessionHistoryLoading = $[50];
    sessionRetryHistory = $[51];
  }
  let t16;
  let t17;
  if ($[52] !== gone || $[53] !== startNewChat) {
    t16 = () => {
      if (gone) {
        startNewChat();
      }
    };
    t17 = [gone, startNewChat];
    $[52] = gone;
    $[53] = startNewChat;
    $[54] = t16;
    $[55] = t17;
  } else {
    t16 = $[54];
    t17 = $[55];
  }
  useEffect(t16, t17);
  const historyLoading = choice === undefined ? isPersisted : onSession ? sessionHistoryLoading : sdk.historyLoading;
  const retryHistory = onSession ? sessionRetryHistory : sdk.retryHistory;
  const engine = useHeldUntilReady(choice === undefined ? null : onSession ? session : sdk.engine, contextReady && !historyLoading && retryHistory === null);
  if (!engaged && engine.messages.length > 0) {
    setEngaged(true);
  }
  let t18;
  if ($[56] !== readSessionId) {
    t18 = readSessionId !== null ? <CodeSessionSync sessionId={readSessionId} /> : null;
    $[56] = readSessionId;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  let t19;
  if ($[58] !== onSession) {
    t19 = onSession ? <InternalAgentPill /> : null;
    $[58] = onSession;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  let t20;
  if ($[60] !== sandbox) {
    t20 = sandbox ? <SandboxStartLine status={sandbox} /> : null;
    $[60] = sandbox;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  let t21;
  if ($[62] !== aiContext || $[63] !== engine || $[64] !== historyLoading || $[65] !== id || $[66] !== liveFinishedMessageId || $[67] !== retryHistory || $[68] !== sdk.savedMessages || $[69] !== t19 || $[70] !== t20) {
    t21 = <ChatRunnerView id={id} initialMessages={sdk.savedMessages} aiContext={aiContext} engine={engine} liveFinishedMessageId={liveFinishedMessageId} historyLoading={historyLoading} onRetryHistory={retryHistory} composerTools={t19} composerStatus={t20} />;
    $[62] = aiContext;
    $[63] = engine;
    $[64] = historyLoading;
    $[65] = id;
    $[66] = liveFinishedMessageId;
    $[67] = retryHistory;
    $[68] = sdk.savedMessages;
    $[69] = t19;
    $[70] = t20;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  let t22;
  if ($[72] !== t18 || $[73] !== t21) {
    t22 = <>{t18}{t21}</>;
    $[72] = t18;
    $[73] = t21;
    $[74] = t22;
  } else {
    t22 = $[74];
  }
  return t22;
}

/** A code session answers the chat only in cubic's own installations (`isPrPageSessionInstallation`). */
function _temp3(state_1) {
  return state_1.startNewChat;
}
function _temp2(state_0) {
  return state_0.setCodeSessionId;
}
function _temp(state) {
  return state.codeSessionId;
}
function InternalAgentPill() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Pill text="Internal" tooltipText="Runs on /code. Only enabled for internal installations" className="shrink-0 px-1.5 py-0.5 text-[11px]" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/** The session's sandbox starting, above the composer until it is ready. */
function SandboxStartLine(t0) {
  const $ = _c(2);
  const {
    status
  } = t0;
  const t1 = status.step ? `${status.title} · ${status.step}` : status.title;
  let t2;
  if ($[0] !== t1) {
    t2 = <Shimmer as="span" className="truncate text-xs text-muted-foreground">{t1}</Shimmer>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}
function sameChoice(current: PageChatEngineChoice, next: PageChatEngineChoice): boolean {
  return current.engine === 'code_session' ? next.engine === 'code_session' && next.sessionId === current.sessionId : next.engine === current.engine;
}